import { useCallback, useState } from "react";

type Options = {
  /** Initial width in pixels. */
  initial?: number;
  /** Minimum width in pixels. */
  min?: number;
  /** Maximum width in pixels (defaults to viewport - 80). */
  max?: number;
};

/**
 * Stateful width + onMouseDown handler for resizable side drawers.
 *
 * Usage:
 *   const { width, startResize } = useResizableDrawer({ initial: 900 });
 *   <aside style={{ width, position: "relative" }}>
 *     <ResizeHandle onMouseDown={startResize} />
 *     ...
 *   </aside>
 */
export function useResizableDrawer(opts: Options = {}) {
  const { initial = 720, min = 360 } = opts;
  const [width, setWidth] = useState(initial);

  const startResize = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const maxWidth = opts.max ?? window.innerWidth - 80;

      const onMove = (ev: MouseEvent) => {
        const next = Math.max(min, Math.min(maxWidth, window.innerWidth - ev.clientX));
        setWidth(next);
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [min, opts.max],
  );

  return { width, startResize };
}

/**
 * Slim absolute-positioned strip on the drawer's left edge that starts the
 * resize gesture. The parent <aside> must be `position: relative`.
 */
export function ResizeHandle({
  onMouseDown,
}: {
  onMouseDown: (event: React.MouseEvent) => void;
}) {
  return (
    <div
      onMouseDown={onMouseDown}
      title="Arrastra para cambiar el tamaño"
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: 6,
        cursor: "col-resize",
        zIndex: 5,
        background: "transparent",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(0, 105, 217, 0.18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    />
  );
}
